import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "../_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, Recycle, ArrowLeftRight, SlidersHorizontal, ClipboardCheck, Trash2, ArrowRight, Factory, Search,
} from "lucide-react";
import { toast } from "sonner";

const money2 = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const unitLabel = (u) => (u === "m3" ? "m³" : u === "load" ? "loads" : "t");
const PROCESS_TYPES = [
  { v: "CRUSH", l: "Crush" }, { v: "SCREEN", l: "Screen" }, { v: "BLEND", l: "Blend" }, { v: "OTHER", l: "Other" },
];

function useStockpiles(open) {
  const [rows, setRows] = useState([]);
  const reload = useCallback(() => api.get("/sy/stockpiles").then((r) => setRows(r.data.stockpiles)).catch(() => {}), []);
  useEffect(() => { if (open) reload(); }, [open, reload]);
  return rows;
}

// ------------------------------- Adjust -------------------------------
export function AdjustDialog({ open, onOpenChange, presetId, onDone }) {
  const rows = useStockpiles(open);
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF({ stockpile_id: presetId || "", dir: "add" }); }, [open, presetId]);
  const sp = rows.find((s) => s.id === f.stockpile_id);
  const submit = async () => {
    if (!f.stockpile_id) return toast.error("Select a stockpile");
    const qty = Number(f.quantity || 0);
    if (!qty) return toast.error("Enter a quantity");
    if (!f.reason) return toast.error("Enter a reason");
    const signed = f.dir === "remove" ? -Math.abs(qty) : Math.abs(qty);
    try {
      await api.post("/sy/adjustments", { stockpile_id: f.stockpile_id, quantity: signed, reason: f.reason, notes: f.notes });
      toast.success("Stock adjusted"); onOpenChange(false); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Adjustment failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sy-adjust-dialog">
        <DialogHeader>
          <DialogTitle className="uppercase font-display text-2xl flex items-center gap-2"><SlidersHorizontal className="w-5 h-5 text-[#FF5F15]" /> Stock adjustment</DialogTitle>
          <DialogDescription>Manually correct a stockpile balance. Recorded on the immutable ledger.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase font-bold">Stockpile *</Label>
            <Select value={f.stockpile_id || ""} onValueChange={(v) => setF({ ...f, stockpile_id: v })} disabled={!!presetId}>
              <SelectTrigger data-testid="adjust-stockpile"><SelectValue placeholder="Select stockpile" /></SelectTrigger>
              <SelectContent>{rows.map((s) => <SelectItem key={s.id} value={s.id}>{s.code} — {s.contents_name} ({s.current_quantity}{unitLabel(s.unit)})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase font-bold">Direction</Label>
              <div className="flex gap-1.5 mt-1">
                {[{ v: "add", l: "Add" }, { v: "remove", l: "Remove" }].map((d) => (
                  <button key={d.v} onClick={() => setF({ ...f, dir: d.v })} className={`flex-1 px-3 py-2 rounded-md text-xs uppercase font-bold border ${f.dir === d.v ? "bg-[#FF5F15] border-[#FF5F15] text-white" : "border-zinc-300 dark:border-white/15 text-zinc-600 dark:text-zinc-300"}`} data-testid={`adjust-dir-${d.v}`}>{d.l}</button>
                ))}
              </div>
            </div>
            <div><Label className="text-xs uppercase font-bold">Quantity ({unitLabel(sp?.unit)}) *</Label><Input type="number" value={f.quantity || ""} onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="adjust-quantity" /></div>
          </div>
          <div><Label className="text-xs uppercase font-bold">Reason *</Label><Input value={f.reason || ""} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="e.g. spillage, moisture loss, recount" data-testid="adjust-reason" /></div>
          <div><Label className="text-xs uppercase font-bold">Notes</Label><Textarea rows={2} value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={submit} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase text-xs font-bold tracking-wider" data-testid="adjust-submit">Save adjustment</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------- Transfer -------------------------------
export function TransferDialog({ open, onOpenChange, presetId, onDone }) {
  const rows = useStockpiles(open);
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF({ from_stockpile_id: presetId || "" }); }, [open, presetId]);
  const src = rows.find((s) => s.id === f.from_stockpile_id);
  const dests = rows.filter((s) => s.id !== f.from_stockpile_id && (!src || s.unit === src.unit));
  const submit = async () => {
    if (!f.from_stockpile_id || !f.to_stockpile_id) return toast.error("Pick both stockpiles");
    const qty = Number(f.quantity || 0);
    if (qty <= 0) return toast.error("Enter a quantity");
    try {
      await api.post("/sy/transfers", { from_stockpile_id: f.from_stockpile_id, to_stockpile_id: f.to_stockpile_id, quantity: qty, notes: f.notes });
      toast.success("Stock transferred"); onOpenChange(false); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Transfer failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sy-transfer-dialog">
        <DialogHeader>
          <DialogTitle className="uppercase font-display text-2xl flex items-center gap-2"><ArrowLeftRight className="w-5 h-5 text-[#FF5F15]" /> Transfer stock</DialogTitle>
          <DialogDescription>Move quantity between two stockpiles of the same unit.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase font-bold">From *</Label>
            <Select value={f.from_stockpile_id || ""} onValueChange={(v) => setF({ ...f, from_stockpile_id: v, to_stockpile_id: "" })} disabled={!!presetId}>
              <SelectTrigger data-testid="transfer-from"><SelectValue placeholder="Source stockpile" /></SelectTrigger>
              <SelectContent>{rows.map((s) => <SelectItem key={s.id} value={s.id}>{s.code} — {s.contents_name} ({s.current_quantity}{unitLabel(s.unit)})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase font-bold">To *</Label>
            <Select value={f.to_stockpile_id || ""} onValueChange={(v) => setF({ ...f, to_stockpile_id: v })}>
              <SelectTrigger data-testid="transfer-to"><SelectValue placeholder="Destination stockpile" /></SelectTrigger>
              <SelectContent>{dests.map((s) => <SelectItem key={s.id} value={s.id}>{s.code} — {s.contents_name} ({s.current_quantity}{unitLabel(s.unit)})</SelectItem>)}</SelectContent>
            </Select>
            {src && dests.length === 0 && <p className="text-[11px] text-amber-600 mt-1">No other {unitLabel(src.unit)} stockpiles to transfer to.</p>}
          </div>
          <div><Label className="text-xs uppercase font-bold">Quantity ({unitLabel(src?.unit)}) *</Label><Input type="number" value={f.quantity || ""} onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="transfer-quantity" />
            {src && <p className="text-[11px] text-zinc-400 mt-1">Available: {src.current_quantity}{unitLabel(src.unit)}</p>}</div>
          <div><Label className="text-xs uppercase font-bold">Notes</Label><Textarea rows={2} value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={submit} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase text-xs font-bold tracking-wider" data-testid="transfer-submit">Transfer stock</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------- Stocktake -------------------------------
export function StocktakeDialog({ open, onOpenChange, presetId, onDone }) {
  const rows = useStockpiles(open);
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF({ stockpile_id: presetId || "" }); }, [open, presetId]);
  const sp = rows.find((s) => s.id === f.stockpile_id);
  const system = sp ? Number(sp.current_quantity || 0) : 0;
  const counted = f.counted_quantity !== undefined && f.counted_quantity !== "" ? Number(f.counted_quantity) : null;
  const variance = counted != null ? +(counted - system).toFixed(3) : null;
  const submit = async () => {
    if (!f.stockpile_id) return toast.error("Select a stockpile");
    if (counted == null) return toast.error("Enter the counted quantity");
    try {
      await api.post("/sy/stocktakes", { stockpile_id: f.stockpile_id, counted_quantity: counted, notes: f.notes });
      toast.success("Stocktake recorded"); onOpenChange(false); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Stocktake failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sy-stocktake-dialog">
        <DialogHeader>
          <DialogTitle className="uppercase font-display text-2xl flex items-center gap-2"><ClipboardCheck className="w-5 h-5 text-[#FF5F15]" /> Stocktake</DialogTitle>
          <DialogDescription>Count a stockpile and record the variance against the system balance.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase font-bold">Stockpile *</Label>
            <Select value={f.stockpile_id || ""} onValueChange={(v) => setF({ ...f, stockpile_id: v })} disabled={!!presetId}>
              <SelectTrigger data-testid="stocktake-stockpile"><SelectValue placeholder="Select stockpile" /></SelectTrigger>
              <SelectContent>{rows.map((s) => <SelectItem key={s.id} value={s.id}>{s.code} — {s.contents_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-3 items-end">
            <div><Label className="text-xs uppercase font-bold">System</Label><div className="h-10 flex items-center font-mono text-sm">{system}{unitLabel(sp?.unit)}</div></div>
            <div><Label className="text-xs uppercase font-bold">Counted *</Label><Input type="number" value={f.counted_quantity ?? ""} onChange={(e) => setF({ ...f, counted_quantity: e.target.value })} data-testid="stocktake-counted" /></div>
            <div><Label className="text-xs uppercase font-bold">Variance</Label><div className={`h-10 flex items-center font-mono text-sm font-bold ${variance == null ? "text-zinc-400" : variance === 0 ? "text-emerald-600" : variance > 0 ? "text-emerald-600" : "text-red-600"}`}>{variance == null ? "—" : `${variance > 0 ? "+" : ""}${variance}${unitLabel(sp?.unit)}`}</div></div>
          </div>
          <div><Label className="text-xs uppercase font-bold">Notes</Label><Textarea rows={2} value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={submit} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase text-xs font-bold tracking-wider" data-testid="stocktake-submit">Record stocktake</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------- Batch (processing) -------------------------------
export function BatchDialog({ open, onOpenChange, onDone }) {
  const rows = useStockpiles(open);
  const [f, setF] = useState({});
  const [inputs, setInputs] = useState([{ stockpile_id: "", quantity: "" }]);
  const [outputs, setOutputs] = useState([{ stockpile_id: "", quantity: "" }]);
  useEffect(() => { if (open) { setF({ process_type: "CRUSH" }); setInputs([{ stockpile_id: "", quantity: "" }]); setOutputs([{ stockpile_id: "", quantity: "" }]); } }, [open]);
  const raw = rows.filter((s) => s.material_id || !s.product_id);
  const prodpiles = rows.filter((s) => s.product_id);
  const setRow = (list, setter, i, k, v) => setter(list.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const submit = async () => {
    const cleanIn = inputs.filter((r) => r.stockpile_id && Number(r.quantity) > 0).map((r) => ({ stockpile_id: r.stockpile_id, quantity: Number(r.quantity) }));
    const cleanOut = outputs.filter((r) => r.stockpile_id && Number(r.quantity) > 0).map((r) => ({ stockpile_id: r.stockpile_id, quantity: Number(r.quantity) }));
    if (!cleanIn.length || !cleanOut.length) return toast.error("Add at least one input and one output with quantities");
    try {
      await api.post("/sy/batches", { process_type: f.process_type, name: f.name, notes: f.notes, inputs: cleanIn, outputs: cleanOut });
      toast.success("Processing batch recorded"); onOpenChange(false); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Batch failed"); }
  };
  const RowList = ({ list, setter, options, testPrefix, label }) => (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-xs uppercase font-bold">{label}</Label>
        <button onClick={() => setter([...list, { stockpile_id: "", quantity: "" }])} className="text-xs text-[#FF5F15] font-bold flex items-center gap-1" data-testid={`${testPrefix}-add`}><Plus className="w-3.5 h-3.5" /> Add</button>
      </div>
      <div className="space-y-2">{list.map((r, i) => (
        <div key={i} className="flex gap-2 items-center">
          <Select value={r.stockpile_id || ""} onValueChange={(v) => setRow(list, setter, i, "stockpile_id", v)}>
            <SelectTrigger className="flex-1" data-testid={`${testPrefix}-sp-${i}`}><SelectValue placeholder="Stockpile" /></SelectTrigger>
            <SelectContent>{options.map((s) => <SelectItem key={s.id} value={s.id}>{s.code} — {s.contents_name} ({s.current_quantity}{unitLabel(s.unit)})</SelectItem>)}</SelectContent>
          </Select>
          <Input type="number" placeholder="Qty" value={r.quantity} onChange={(e) => setRow(list, setter, i, "quantity", e.target.value)} className="w-24" data-testid={`${testPrefix}-qty-${i}`} />
          {list.length > 1 && <button onClick={() => setter(list.filter((_, idx) => idx !== i))} className="text-zinc-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>}
        </div>
      ))}</div>
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="sy-batch-dialog">
        <DialogHeader>
          <DialogTitle className="uppercase font-display text-2xl flex items-center gap-2"><Recycle className="w-5 h-5 text-[#FF5F15]" /> New processing batch</DialogTitle>
          <DialogDescription>Consume raw material and produce finished product. Full lineage is kept.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase font-bold">Process</Label>
              <Select value={f.process_type || "CRUSH"} onValueChange={(v) => setF({ ...f, process_type: v })}>
                <SelectTrigger data-testid="batch-process"><SelectValue /></SelectTrigger>
                <SelectContent>{PROCESS_TYPES.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs uppercase font-bold">Name / ref</Label><Input value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Optional" /></div>
          </div>
          <RowList list={inputs} setter={setInputs} options={rows} testPrefix="batch-in" label="Inputs (raw consumed)" />
          <div className="flex justify-center text-[#FF5F15]"><ArrowRight className="w-5 h-5 rotate-90" /></div>
          <RowList list={outputs} setter={setOutputs} options={prodpiles.length ? prodpiles : rows} testPrefix="batch-out" label="Outputs (product produced)" />
          <div><Label className="text-xs uppercase font-bold">Notes</Label><Textarea rows={2} value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={submit} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase text-xs font-bold tracking-wider" data-testid="batch-submit">Record batch</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const mvColor = (t) => (["INBOUND", "TRANSFER_IN", "PROCESS_IN"].includes(t) || false ? "text-emerald-600" : "text-red-600");

// ------------------------------- Processing page -------------------------------
export function SupplyYardProcessing() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { api.get("/sy/batches").then((r) => setRows(r.data.batches)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="px-4 py-4 md:p-6 lg:p-10 md:max-w-[1600px] md:mx-auto pb-24 md:pb-10" data-testid="sy-processing-page">
      <PageHeader tag="supply yard / processing" title="Processing & Recycling" subtitle="Turn raw material into saleable product — every batch keeps its lineage and yield."
        action={<Button onClick={() => setOpen(true)} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase text-xs font-bold tracking-wider" data-testid="sy-new-batch"><Plus className="w-4 h-4 mr-1.5" /> New batch</Button>} />
      {rows.length === 0 ? (
        <EmptyState icon={Factory} title="No batches yet" description="Record a recycling batch to convert raw stockpiles into finished product with full traceability."
          action={<Button onClick={() => setOpen(true)} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase text-xs font-bold tracking-wider">Record first batch</Button>} />
      ) : (
        <div className="space-y-3">{rows.map((b) => (
          <div key={b.id} className="hard-card p-4" data-testid={`sy-batch-${b.batch_no}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg bg-[#FF5F15]/15 flex items-center justify-center"><Recycle className="w-4 h-4 text-[#FF5F15]" /></span>
                <div><div className="font-bold">{b.batch_no} <Badge variant="outline" className="uppercase text-[9px] ml-1">{b.process_type}</Badge></div><div className="text-[11px] text-zinc-400">{b.date}{b.name ? ` · ${b.name}` : ""}</div></div>
              </div>
              {b.yield_pct != null && <div className="text-right"><div className="text-lg font-display font-extrabold text-[#FF5F15]">{b.yield_pct}%</div><div className="text-[10px] uppercase text-zinc-400">yield</div></div>}
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <div className="rounded-lg bg-red-500/5 border border-red-500/15 p-3">
                <div className="text-[10px] uppercase tracking-wider text-red-500 font-bold mb-1">Consumed</div>
                {b.inputs.map((i, idx) => <div key={idx} className="flex justify-between text-sm"><span>{i.code} — {i.name}</span><span className="font-mono">−{i.quantity}{unitLabel(i.unit)}</span></div>)}
              </div>
              <div className="rounded-lg bg-emerald-500/5 border border-emerald-500/15 p-3">
                <div className="text-[10px] uppercase tracking-wider text-emerald-600 font-bold mb-1">Produced</div>
                {b.outputs.map((o, idx) => <div key={idx} className="flex justify-between text-sm"><span>{o.code} — {o.name}</span><span className="font-mono">+{o.quantity}{unitLabel(o.unit)}</span></div>)}
              </div>
            </div>
            {b.notes && <p className="text-xs text-zinc-500 mt-2">{b.notes}</p>}
          </div>
        ))}</div>
      )}
      <BatchDialog open={open} onOpenChange={setOpen} onDone={load} />
    </div>
  );
}

// ------------------------------- Stock Control page -------------------------------
export function SupplyYardStockControl() {
  const [data, setData] = useState({ adjustments: [], transfers: [], stocktakes: [], batches: [] });
  const [dlg, setDlg] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [mvType, setMvType] = useState("");
  const [mvQ, setMvQ] = useState("");
  const load = useCallback(() => { api.get("/sy/stock-control").then((r) => setData(r.data)).catch(() => {}); }, []);
  const loadLedger = useCallback(() => { api.get(`/sy/movements?${mvType ? `type=${mvType}&` : ""}${mvQ ? `q=${encodeURIComponent(mvQ)}` : ""}`).then((r) => setLedger(r.data.movements)).catch(() => {}); }, [mvType, mvQ]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setTimeout(loadLedger, 250); return () => clearTimeout(t); }, [loadLedger]);
  const reloadAll = () => { load(); loadLedger(); };
  const MV_TYPES = ["", "INBOUND", "OUTBOUND", "PROCESSING", "TRANSFER", "ADJUSTMENT", "STOCKTAKE", "REVERSAL"];
  const mvBadge = (t) => {
    const map = { INBOUND: "bg-emerald-500/15 text-emerald-600", OUTBOUND: "bg-[#FF5F15]/15 text-[#FF5F15]",
      PROCESS_IN: "bg-purple-500/15 text-purple-600", PROCESS_OUT: "bg-purple-500/15 text-purple-600",
      TRANSFER_IN: "bg-blue-500/15 text-blue-600", TRANSFER_OUT: "bg-blue-500/15 text-blue-600",
      ADJUSTMENT: "bg-amber-500/15 text-amber-600", STOCKTAKE: "bg-amber-500/15 text-amber-600",
      VOID_REVERSAL: "bg-red-500/15 text-red-500" };
    return map[t] || "bg-zinc-500/15 text-zinc-500";
  };
  return (
    <div className="px-4 py-4 md:p-6 lg:p-10 md:max-w-[1600px] md:mx-auto pb-24 md:pb-10" data-testid="sy-stock-control-page">
      <PageHeader tag="supply yard / stock control" title="Stock Control" subtitle="The full inventory ledger — every movement is immutable and reconcilable." />
      <div className="flex flex-wrap gap-2 mb-5">
        <Button onClick={() => setDlg("adjust")} className="rounded-md bg-[#FF5F15] hover:bg-[#E65100] uppercase text-xs font-bold tracking-wider" data-testid="sc-adjust"><SlidersHorizontal className="w-4 h-4 mr-1.5" /> Adjustment</Button>
        <Button onClick={() => setDlg("transfer")} variant="outline" className="rounded-md uppercase text-xs font-bold tracking-wider" data-testid="sc-transfer"><ArrowLeftRight className="w-4 h-4 mr-1.5" /> Transfer</Button>
        <Button onClick={() => setDlg("stocktake")} variant="outline" className="rounded-md uppercase text-xs font-bold tracking-wider" data-testid="sc-stocktake"><ClipboardCheck className="w-4 h-4 mr-1.5" /> Stocktake</Button>
      </div>

      {/* Full movement ledger */}
      <div className="hard-card p-5 mb-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5F15] font-bold mb-3">Inventory ledger</div>
        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <div className="relative flex-1 max-w-xs"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" /><Input value={mvQ} onChange={(e) => setMvQ(e.target.value)} placeholder="Search stockpile, reason, ref…" className="pl-9 h-8" data-testid="ledger-search" /></div>
          <div className="flex gap-1.5 flex-wrap">{MV_TYPES.map((t) => (
            <button key={t || "all"} onClick={() => setMvType(t)} className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold border ${mvType === t ? "bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 border-transparent" : "border-zinc-300 dark:border-white/15 text-zinc-600 dark:text-zinc-300"}`} data-testid={`ledger-type-${t || "all"}`}>{t || "all"}</button>
          ))}</div>
        </div>
        {ledger.length === 0 ? <p className="text-sm text-zinc-500">No movements match.</p> : (
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto"><table className="w-full text-sm">
            <thead className="sticky top-0 bg-white dark:bg-zinc-900"><tr className="text-[10px] uppercase tracking-wider text-zinc-400 text-left border-b border-zinc-100 dark:border-white/10"><th className="py-2">When</th><th>Type</th><th>Stockpile</th><th>Reason / ref</th><th>By</th><th className="text-right">Qty</th></tr></thead>
            <tbody>{ledger.map((m) => (
              <tr key={m.id} className="border-b border-zinc-50 dark:border-white/5" data-testid={`ledger-row-${m.id}`}>
                <td className="py-1.5 text-zinc-500 whitespace-nowrap">{(m.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td><span className={`px-2 py-0.5 rounded-full text-[9px] uppercase font-bold ${mvBadge(m.movement_type)}`}>{m.movement_type.replace(/_/g, " ")}</span></td>
                <td className="font-bold">{m.stockpile_code}</td>
                <td className="text-zinc-500 truncate max-w-[220px]">{m.reason || m.reference || "—"}</td>
                <td className="text-zinc-500">{m.created_by_name}</td>
                <td className={`text-right font-mono font-bold ${m.quantity >= 0 ? "text-emerald-600" : "text-red-600"}`}>{m.quantity >= 0 ? "+" : ""}{m.quantity}{unitLabel(m.unit)}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="hard-card p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5F15] font-bold mb-3">Adjustments & stocktake variances</div>
          {data.adjustments.length === 0 ? <p className="text-sm text-zinc-500">No adjustments recorded.</p> : (
            <div className="space-y-1.5">{data.adjustments.map((a) => (
              <div key={a.id} className="flex items-center justify-between text-sm py-1.5 border-b border-zinc-100 dark:border-white/5">
                <span className="min-w-0"><span className="font-bold">{a.stockpile_code}</span> <Badge variant="outline" className="uppercase text-[9px] ml-1">{a.movement_type === "STOCKTAKE" ? "stocktake" : "adjust"}</Badge><br /><span className="text-xs text-zinc-500 truncate">{a.reason || a.reference || "—"} · {a.created_by_name}</span></span>
                <span className={`font-mono font-bold shrink-0 ${a.quantity >= 0 ? "text-emerald-600" : "text-red-600"}`}>{a.quantity >= 0 ? "+" : ""}{a.quantity}{unitLabel(a.unit)}</span>
              </div>
            ))}</div>
          )}
        </div>

        <div className="hard-card p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5F15] font-bold mb-3">Transfers</div>
          {data.transfers.length === 0 ? <p className="text-sm text-zinc-500">No transfers recorded.</p> : (
            <div className="space-y-1.5">{data.transfers.map((t) => (
              <div key={t.id} className="flex items-center justify-between text-sm py-1.5 border-b border-zinc-100 dark:border-white/5">
                <span className="flex items-center gap-1.5 min-w-0"><span className="font-bold">{t.from_code}</span><ArrowRight className="w-3.5 h-3.5 text-[#FF5F15] shrink-0" /><span className="font-bold">{t.to_code}</span></span>
                <span className="text-right shrink-0"><span className="font-mono">{t.quantity}{unitLabel(t.unit)}</span><br /><span className="text-[11px] text-zinc-400">{t.date}</span></span>
              </div>
            ))}</div>
          )}
        </div>

        <div className="hard-card p-5 lg:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5F15] font-bold mb-3">Recent stocktakes</div>
          {data.stocktakes.length === 0 ? <p className="text-sm text-zinc-500">No stocktakes recorded.</p> : (
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-400 text-left border-b border-zinc-100 dark:border-white/10"><th className="py-2">Stockpile</th><th>Date</th><th>By</th><th className="text-right">System</th><th className="text-right">Counted</th><th className="text-right">Variance</th></tr></thead>
              <tbody>{data.stocktakes.map((s) => (
                <tr key={s.id} className="border-b border-zinc-50 dark:border-white/5" data-testid={`sc-stocktake-${s.stockpile_code}`}>
                  <td className="py-2 font-bold">{s.stockpile_code}</td><td className="text-zinc-500">{s.date}</td><td className="text-zinc-500">{s.created_by_name}</td>
                  <td className="text-right font-mono">{s.system_quantity}{unitLabel(s.unit)}</td><td className="text-right font-mono">{s.counted_quantity}{unitLabel(s.unit)}</td>
                  <td className={`text-right font-mono font-bold ${s.variance === 0 ? "text-emerald-600" : s.variance > 0 ? "text-emerald-600" : "text-red-600"}`}>{s.variance > 0 ? "+" : ""}{s.variance}{unitLabel(s.unit)}</td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </div>
      </div>

      <AdjustDialog open={dlg === "adjust"} onOpenChange={(v) => !v && setDlg(null)} onDone={reloadAll} />
      <TransferDialog open={dlg === "transfer"} onOpenChange={(v) => !v && setDlg(null)} onDone={reloadAll} />
      <StocktakeDialog open={dlg === "stocktake"} onOpenChange={(v) => !v && setDlg(null)} onDone={reloadAll} />
    </div>
  );
}
